import { useMemo, useState } from 'react';
import {
  DataTable,
  KpiFilterRow,
  TableShell,
  type KpiFilterItem,
  type ViewMode
} from '@repletoryreach/sdk-ui/data-display';
import { Badge, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { useAuditFeed, AUDIT_SOURCES, type AuditRow, type AuditSource } from '../../../../data/useAuditFeed';
import { usePersistedState } from '../../../../lib/usePersistedState';
import type { Indexed } from '../../../../lib/types';
import { formatDateTime, formatRelative } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';

const MUTED = 'var(--rr-color-text-muted)';

// Audit rows are read-only: nothing here opens a detail page. The row keeps a
// card's border and ground but none of the hover lift, so it stops advertising
// a click that does not exist.
const STATIC_ROW: React.CSSProperties = {
  background: 'var(--rr-color-surface)',
  border: '1px solid var(--rr-color-border)',
  borderRadius: 12,
  padding: '12px 16px'
};

type KpiKey = 'all' | AuditSource;

export function AuditTab({ tenantId }: { tenantId: string }) {
  const t = useT();
  const since = useMemo(() => new Date(Date.now() - 7 * 86_400_000).toISOString(), []);
  const enabled = useMemo(
    () => Object.fromEntries(AUDIT_SOURCES.map((s) => [s, true])) as Record<AuditSource, boolean>,
    []
  );
  const { rows, loading } = useAuditFeed(since, enabled, 200);
  const scoped = useMemo(
    () => rows.filter((r) => r.organisation_id === tenantId),
    [rows, tenantId]
  );

  const [view, setView] = usePersistedState<ViewMode>('admin.tenant.audit.view', 'table');
  const [query, setQuery] = useState('');
  const [kpi, setKpi] = useState<KpiKey | null>('all');

  const sourceLabel = (source: AuditSource) => t(`auditSource.${source}`, source);

  const counts = useMemo(() => {
    const c: Record<KpiKey, number> = { all: scoped.length } as Record<KpiKey, number>;
    for (const src of AUDIT_SOURCES) c[src] = scoped.filter((r) => r.source === src).length;
    return c;
  }, [scoped]);

  const kpiItems: KpiFilterItem[] = [
    { id: 'all', label: t('common.all'), value: counts.all, tone: 'primary' },
    ...AUDIT_SOURCES.map((src) => ({
      id: src,
      label: sourceLabel(src),
      value: counts[src],
      tone: 'pending' as const
    }))
  ];

  const filtered = useMemo(() => {
    let r = scoped;
    if (kpi && kpi !== 'all') r = r.filter((row) => row.source === kpi);
    const q = query.trim().toLowerCase();
    if (q) r = r.filter((row) => row.summary.toLowerCase().includes(q));
    return r;
  }, [scoped, kpi, query]);

  return (
    <TableShell
      title={t('tenants.view.audit.title')}
      description={t('tenants.view.audit.description')}
      totalLabel={t('tenants.view.audit.totalLabel', { count: filtered.length, total: scoped.length })}
      kpis={
        <KpiFilterRow
          items={kpiItems}
          value={kpi}
          onChange={(next) => setKpi((next ?? 'all') as KpiKey)}
        />
      }
      search={query}
      onSearch={setQuery}
      searchPlaceholder={t('tenants.view.audit.searchPlaceholder')}
      view={view}
      onViewChange={setView}
      loading={loading}
      isEmpty={filtered.length === 0}
      empty={
        <Text role="bodySmall" c={MUTED}>
          {t('tenants.view.audit.empty')}
        </Text>
      }
    >
      {view === 'table' && (
        <DataTable<Indexed<AuditRow>>
          records={filtered as Indexed<AuditRow>[]}
          loading={loading}
          idAccessor={(r, i) => `${r.source}-${r.id}-${i}`}
          emptyState={t('tenants.view.audit.empty')}
          columns={[
            { accessor: 'occurred_at', title: t('tenants.view.audit.columns.when'), render: (r) => formatDateTime(r.occurred_at) },
            {
              accessor: 'source',
              title: t('tenants.view.audit.columns.source'),
              render: (r) => <Badge size="xs" variant="outline">{sourceLabel(r.source)}</Badge>
            },
            { accessor: 'summary', title: t('tenants.view.audit.columns.summary') }
          ]}
        />
      )}
      {view === 'list' && (
        <Stack gap="sm">
          {filtered.map((r, i) => (
            <div key={`${r.source}-${r.id}-${i}`} style={STATIC_ROW}>
              <Group gap="md" align="center" wrap="nowrap" style={{ width: '100%' }}>
                <Badge size="xs" variant="outline" style={{ flexShrink: 0 }}>
                  {sourceLabel(r.source)}
                </Badge>
                <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
                  <Text role="subtitle" fw={500} truncate>
                    {r.summary}
                  </Text>
                  <Text role="caption" c={MUTED}>
                    {formatDateTime(r.occurred_at)} ({formatRelative(r.occurred_at)})
                  </Text>
                </Stack>
              </Group>
            </div>
          ))}
        </Stack>
      )}
      {view === 'cards' && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', // i18n-ignore: CSS grid track sizing, not copy
            gap: 16
          }}
        >
          {filtered.map((r, i) => (
            <div key={`${r.source}-${r.id}-${i}`} style={{ ...STATIC_ROW, borderRadius: 8, padding: 16 }}>
              <Stack gap="sm">
                <Group justify="space-between" align="baseline">
                  <Badge size="xs" variant="outline">{sourceLabel(r.source)}</Badge>
                  <Text role="caption" c={MUTED}>
                    {formatRelative(r.occurred_at)}
                  </Text>
                </Group>
                <Text role="bodySmall">{r.summary}</Text>
                <Text role="caption" c={MUTED}>
                  {formatDateTime(r.occurred_at)}
                </Text>
              </Stack>
            </div>
          ))}
        </div>
      )}
    </TableShell>
  );
}
